import React, { useEffect, useState, useCallback } from "react";
import { useExpanded, useFlexLayout, usePagination, useResizeColumns, useSortBy, useTable } from "react-table";
import { QueryClient, QueryClientProvider, useQuery } from 'react-query'
import { Col, Row } from 'reactstrap';
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import {
  reducer,
  useEffectToDispatch,
  fetchApiData,
  fetchApiDataExport,
  initialState,
  Sorting
} from "@/shared/apiTableHelper";
import { Sticky, StickyContainer } from 'react-sticky';
import { dateStrToYmd, getFirstDay, getReportName} from "@/shared/helpers";
import { tableColumns } from "./columns";
import SupplierRedemptionFilter from "./SupplierRedemptionFilter";

const queryClient = new QueryClient()

const defaultFrom = getFirstDay()
const defaultTo = new Date()

const DataTable = ({ organization, merchants, programs }) => {
    
    const [filter, setFilter] = useState({ 
        merchants: merchants, 
        programs: programs,
        from: dateStrToYmd(defaultFrom),
        to: dateStrToYmd(defaultTo),
        active: true,
        reportKey: 'sku_value',
        inventoryType: 0,
    });
    const [useFilter, setUseFilter] = useState(false);
    const [trigger, setTrigger] = useState(0);
    const [exportData, setExportData] = useState([]);
    const [exportHeaders, setExportHeaders] = useState([]);
    const [exportToCsv, setExportToCsv] = useState(false);
    const [filterValues, setFilterValues] = useState([]);
    const [columns, setColumns] = useState([]);
    const exportLink = React.createRef();
    const [isExporting, setIsExporting] = useState(false);
    const [{ queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger }, dispatch] =
      React.useReducer(reducer, initialState);
  
    // Why: the filter panel rebuilds the filter from its own fields on every
    //      Filter press, and the program scope is not one of its fields — without
    //      this, the first press would silently widen the report to every program.
    // Scope: a no-op when the table is not scoped (the global report passes no
    //        programs, and an undefined key is dropped from the request).
    // SRP: keeps the caller's program scope on whatever filter the panel emits.
    const setFilterKeepingScope = (next) => setFilter({ ...next, programs });

    const apiUrl = `/organization/${organization.id}/report/supplier-redemption`;
    const { isLoading, error, data, isSuccess } = useQuery(
      ['', apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
      () => fetchApiData(
        {
          url: apiUrl,
          page: queryPageIndex,
          size: queryPageSize,
          filter,
          sortby: queryPageSortBy,
          trigger: queryTrigger
        }
      ),
      {
        keepPreviousData: true,
        staleTime: Infinity,
      }
    );
  
    useEffect(() => {
      if (exportToCsv) {
        if (exportLink.current) {
          setExportToCsv(false);
          exportLink.current.link.click();
        }
      }
    }, [exportLink])
  
  const download = async (filterValues) => {
    try {
      if (!data?.results) {
        console.error("No results found in data");
        return;
      }

      // Always convert to array
      const resultsArray = Object.values(data.results);

      // Include the total row
      const csvData = [...resultsArray, data.config.total];

      setIsExporting(true);

      // Deep copy and normalize values
      const cleanedData = csvData.map((item) => {
        const newItem = { ...item };

        // Fix % formatting
        const needsFix =
          newItem.avg_discount_percent &&
          !newItem.avg_discount_percent.toString().endsWith("%");

        if (needsFix) {
          newItem.avg_discount_percent += "%";
          newItem.percent_total_cost += "%";
          newItem.percent_total_redemption_value += "%";

          newItem.total_cost_basis = "$" + newItem.total_cost_basis;
          newItem.total_premium = "$" + newItem.total_premium;
          newItem.total_redemption_value = "$" + newItem.total_redemption_value;
        }

        return newItem;
      });

      // Send data to your CSV component
      setExportData(cleanedData);
      setExportHeaders(data.config.columns);

      // Trigger CSV download
      setExportToCsv(true);

    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setTimeout(() => setIsExporting(false), 300);
    }
  };
  
    const updateColumns = useCallback((data) => {
      if (data) {
        setColumns(tableColumns(data.config));
      }
    }, []);
  
  
    useEffect(() => {
      updateColumns(data);
    }, [updateColumns, data]);
    
    const totalPageCount = Math.ceil(totalCount / queryPageSize)
  
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      footerGroups,
      rows,
      prepareRow,
      page,
      pageCount,
      pageOptions,
      gotoPage,
      previousPage,
      canPreviousPage,
      nextPage,
      canNextPage,
      setPageSize,
      state: { pageIndex, pageSize, sortBy }
    } = useTable({
      columns: columns,
      data: data ?  Object.values(data.results) : [], //data: data ? data.results : [],
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true, // Tell the usePagination
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
      disableResizing: true,
      autoResetHiddenColumns: false
    },
      useSortBy,
      useExpanded,
      usePagination,
      useResizeColumns,
      useFlexLayout,
    );
  
    const manualPageSize = []
    useEffectToDispatch(dispatch, { pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger });
  
    if (isLoading) {
      return <p>Loading...</p>;
    }
  
    if (error) {
      return <p>Error: {JSON.stringify(error)}</p>;
    }
  
    if (isSuccess)
      return (
        <StickyContainer>
          <div style={{ width: '100%', overflowX: 'auto', margin: 'auto' }}>
            <div className='table react-table report-table' style={{ minHeight: '10px' }}>
              <div className="action-panel" style={{ paddingBottom: '35px' }}>
                <Row className="mx-0">
                  <Col>
                    <SupplierRedemptionFilter
                        filter={filter} setFilter={setFilterKeepingScope} useFilter={useFilter} setUseFilter={setUseFilter}
                        exportData={exportData} exportLink={exportLink} exportHeaders={exportHeaders}
                        download={download} isExporting={isExporting} reportName={getReportName('supplier_redemption')}/>
                  </Col>
                </Row>
                <div style={{ clear: 'both' }}>&nbsp;</div>
              </div>
            </div>
            {isLoading &&
              <p>Loading...</p>
            }
            {isSuccess &&
              <table {...getTableProps()} className="table">
  
                <Sticky topOffset={90}>
                  {({ style }) => (
                    <thead style={{ ...style, top: '60px', zIndex: 99999 }}>
                      {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map(column => (
                            <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                              {column.render('Header')}
                              {column.isSorted ? <Sorting column={column} /> : ''}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                  )}
                </Sticky>
                <tbody className="table table--bordered" {...getTableBodyProps()}>
                  {page.map(row => {
                    prepareRow(row);
                    const subCount = (row.id.match(/\./g) || []).length
                    return (
                      <tr {...row.getRowProps()}>
                        {
                          row.cells.map(cell => {
                            // console.log(cell)
                            const paddingLeft = subCount * 20
                            return <td {...cell.getCellProps({
                              className: cell.column.className,
                              style: {
                                // textAlign: cell.column.noAlignRight ? '' : 'right',
                              }
                            })}><span
                              style={cell.column.Header === '#' ? { paddingLeft: `${paddingLeft}px` } : null}>{cell.render('Cell')}</span>
                            </td>
                          })
                        }
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  {footerGroups.map((footerGroup) => (
                    <tr {...footerGroup.getFooterGroupProps()}>
                      {footerGroup.headers.map(column => (
                        <th {...column.getFooterProps()}>{column.render('Footer')}</th>
                      ))}
                    </tr>
                  ))}
                </tfoot>
              </table>
            }
          </div>
        </StickyContainer>
      )
  }

const TableWrapper = ({ organization, merchants, programs }) => {
if (!organization) return 'Loading...'
return (
    <QueryClientProvider client={queryClient}>
        <DataTable organization={organization} merchants={merchants} programs={programs} />
    </QueryClientProvider>
)
}

export default withRouter(connect((state) => ({
    organization: state.organization
}))(TableWrapper));
